import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useBulkUpdateUoms } from 'companySettings/hooks';
import { IUomConversionAuditRow } from 'companySettings/types';
import { FormProvider, useNotificationContext } from 'core/states';

import { BulkUpdateUomsFormFields } from './BulkUpdateUomsFormFields';
import { getSelectedPartIds, mapBulkUpdateUomsPayload } from './mappers';
import { bulkUpdateUomsSchema } from './schema';
import { IBulkUpdateUomsForm } from './types';

interface IProps {
  selectedRows: IUomConversionAuditRow[];
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

// Every checkbox starts off, so an accidental submit writes nothing.
const defaultValues: IBulkUpdateUomsForm = {
  editAlternateSalesUom: false,
  editAlternateUom: false,
  editExternalUom: false,
  editPackageUom: false,
  editSalesUom: false,
  editUom: false,
};

export const BulkUpdateUoms = ({ selectedRows, isOpen, onClose, onSubmitSuccess }: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { bulkUpdateUoms } = useBulkUpdateUoms();

  const partCount = useMemo(() => getSelectedPartIds(selectedRows).length, [selectedRows]);

  const handleOnClose = (reset?: UseFormReset<IBulkUpdateUomsForm>) => {
    clearNotification();
    onClose();
    reset?.(defaultValues);
  };

  const onSubmit =
    (reset?: UseFormReset<IBulkUpdateUomsForm>) => async (data: IBulkUpdateUomsForm) => {
      const payload = mapBulkUpdateUomsPayload(data, selectedRows);

      if (0 === Object.keys(payload.uoms).length) {
        return;
      }

      const result = await bulkUpdateUoms(payload);

      if (!result?.success) {
        return;
      }

      onSubmitSuccess?.();

      // Wait for the success notification to be read before the drawer closes.
      setTimeout(() => handleOnClose(reset), 1000);
    };

  return (
    <FormProvider schema={bulkUpdateUomsSchema()} defaultValues={defaultValues} resetDefaultValues>
      <BulkUpdateUomsFormFields
        partCount={partCount}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
